import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { getFormValues } from 'redux-form';
import { connect } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';

import { v1 } from 'uuid';
import jobProfileCoreQuery from './JobProfileDetailQuery';
import JobProfileDetail from './JobProfileDetail';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import ConfirmModal from './JobProfileDeleteConfirmModal';
import {
  removeJobProfile,
  AddCSRespondent,
  useJPSHint, useSJLHint, useCSHint,
  suggestJobProfileDimension, jobProfileToggleCS, jobProfileToggleJPS,
} from '../../../services/apps/JobProfileService';
import { GRAPHQL_EXCEPTION_CODE } from '../../../shared/Constants';
import ArrayUtil from '../../../utils/ArrayUtil';
import { withAccount } from '../../../components/AccountContext';
import { withGrowl } from '../../../components/GrowlNotifications';
import {
  intlShape, growlShape, matchShape, historyShape,
} from '../../../components/prop-types';

const mapStateToProps = state => ({ sjl: getFormValues('sjl')(state) });

const messages = defineMessages({
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
});

class JobProfileDetailContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hintsCS: null,
      hintsJPS: null,
      hintsSJL: null,
      isSectionEditable: false,
      selectedHints: {
        isUsingTopPerformerHints: false,
        isUsingJobProfileSurveyHints: false,
        standardJobProfileNumber: null,
      },
      suggestions: {
        data: null,
      },
      isDataLoading: false,
      showDeleteConfirmModal: false,
    };

    this.toggleSectionEditable = this.toggleSectionEditable.bind(this);

    this.handleAddCSRespondent = this.handleAddCSRespondent.bind(this);
    this.handleToggleCS = this.handleToggleCS.bind(this);
    this.handleHintsCS = this.handleHintsCS.bind(this);

    this.handleToggleJPS = this.handleToggleJPS.bind(this);
    this.handleHintsJPS = this.handleHintsJPS.bind(this);

    this.handleHintsSJL = this.handleHintsSJL.bind(this);

    this.handleSuggestJobProfile = this.handleSuggestJobProfile.bind(this);
    this.deleteJobProfile = this.deleteJobProfile.bind(this);
    this.cancelDeletingJobProfile = this.cancelDeletingJobProfile.bind(this);
    this.processDeletingJobProfile = this.processDeletingJobProfile.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { error } = nextProps.jobProfileCore;
    if (error) {
      const ex = {
        err: error,
        type: GRAPHQL_EXCEPTION_CODE,
      };
      throw ex;
    }
  }

  toggleSectionEditable() {
    const { isSectionEditable } = this.state;
    this.setState({ isSectionEditable: !isSectionEditable });
  }

  async handleAddCSRespondent(formValues) {
    const {
      growl, currentAccount, intl, match,
    } = this.props;
    const { formatMessage } = intl;
    try {
      const response = await AddCSRespondent({
        jobProfileId: match.params.jobProfileId,
        respondentId: formValues.respondentId,
        accountId: currentAccount,
      });
      Promise.resolve(response);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }
  }

  async handleToggleCS(id) {
    const { match, growl } = this.props;
    await jobProfileToggleCS({
      id,
      jobProfileId: match.params.jobProfileId,
    })
      .then((response) => {
        Promise.resolve(response);
      })
      .catch((errors) => {
        growlErrors(growl, errors);
      });
  }

  async handleHintsCS(isShowHint) {
    const { growl, currentAccount, match } = this.props;
    let hintsCS = null;
    const { selectedHints } = this.state;
    await this.setState({ isDataLoading: true });
    if (isShowHint) {
      try {
        const result = await useCSHint({
          jobProfileId: match.params.jobProfileId,
          accountId: currentAccount,
        });

        // eslint-disable-next-line max-len
        const haveData = result
          && result.getConcurrentStudyHints
          && ArrayUtil.isNotEmpty(result.getConcurrentStudyHints.hints);
        hintsCS = haveData ? result.getConcurrentStudyHints.hints : null;
        if (hintsCS) {
          selectedHints.isUsingTopPerformerHints = true;
        }
      } catch (errors) {
        growlErrors(growl, errors);
      }
    } else {
      selectedHints.isUsingTopPerformerHints = false;
    }
    this.setState({ hintsCS, selectedHints, isDataLoading: false });
  }

  async handleToggleJPS(id) {
    const { match, growl } = this.props;
    try {
      await jobProfileToggleJPS({
        id,
        jobProfileId: match.params.jobProfileId,
      });
    } catch (errors) {
      growlErrors(growl, errors);
    }
  }

  async handleHintsJPS(isShowHint) {
    const { growl, currentAccount, match } = this.props;
    let hintsJPS = null;
    const { selectedHints } = this.state;
    await this.setState({ isDataLoading: true });
    if (isShowHint) {
      try {
        const result = await useJPSHint({
          jobProfileId: match.params.jobProfileId,
          accountId: currentAccount,
        });

        // eslint-disable-next-line max-len
        const haveData = result && result.getJobProfileSurveyHints && ArrayUtil.isNotEmpty(result.getJobProfileSurveyHints.hints);
        hintsJPS = haveData ? result.getJobProfileSurveyHints.hints : null;
        if (hintsJPS) {
          selectedHints.isUsingJobProfileSurveyHints = true;
        }
      } catch (errors) {
        growlErrors(growl, errors);
      }
    } else {
      selectedHints.isUsingJobProfileSurveyHints = false;
    }

    this.setState({ hintsJPS, selectedHints, isDataLoading: false });
  }

  async handleHintsSJL(isShowHint, standardJobProfileNumber) {
    const { growl, currentAccount } = this.props;
    let hintsSJL = null;
    const { selectedHints } = this.state;
    await this.setState({ isDataLoading: true });
    if (standardJobProfileNumber && isShowHint) {
      try {
        const result = await useSJLHint({
          standardJobProfileNumber,
          accountId: currentAccount,
        });

        const haveData = result && result.getStandardJobHints;
        hintsSJL = haveData ? result.getStandardJobHints : null;
        if (hintsSJL) {
          selectedHints.standardJobProfileNumber = standardJobProfileNumber;
        }
      } catch (errors) {
        growlErrors(growl, errors);
      }
    } else {
      selectedHints.standardJobProfileNumber = null;
    }

    this.setState({ hintsSJL, selectedHints, isDataLoading: false });
  }

  async handleSuggestJobProfile() {
    const {
      currentAccount, match, intl, growl,
    } = this.props;
    const { selectedHints } = this.state;
    const { standardJobProfileNumber, isUsingTopPerformerHints, isUsingJobProfileSurveyHints } = selectedHints;

    if (standardJobProfileNumber || isUsingTopPerformerHints || isUsingJobProfileSurveyHints) {
      const { jobProfileId } = match.params;
      const { formatMessage } = intl;
      try {
        this.setState({ isDataLoading: true });
        const res = await suggestJobProfileDimension({
          jobProfileId,
          standardJobProfileNumber,
          isUsingTopPerformerHints,
          isUsingJobProfileSurveyHints,
          accountId: currentAccount,
        });
        growlActionSuccess(growl, formatMessage(messages.successNoti));
        const { suggestJobProfileHints } = res;
        this.setState({
          suggestions: {
            timestamp: v1(),
            data: suggestJobProfileHints,
          },
          isSectionEditable: false,
          isDataLoading: false,
        });
      } catch (errors) {
        growlErrors(growl, errors);
        this.setState({ isDataLoading: false });
      }
    }
  }

  deleteJobProfile() {
    this.setState({ showDeleteConfirmModal: true });
  }

  cancelDeletingJobProfile() {
    this.setState({ showDeleteConfirmModal: false });
  }

  async processDeletingJobProfile() {
    const {
      match, history, growl, currentAccount,
    } = this.props;
    const { jobProfileId } = match.params ? match.params : null;
    if (jobProfileId) {
      try {
        await removeJobProfile({
          jobProfileId,
          accountId: currentAccount,
        });
        const listUrl = `/accounts/${currentAccount}/job-profiles`;
        history.push(listUrl);
      } catch (errors) {
        growlErrors(growl, errors);
      }
    }
    this.setState({ showDeleteConfirmModal: false });
  }

  render() {
    const { match, currentAccount, match: { params } } = this.props;
    const {
      hintsCS,
      hintsJPS,
      hintsSJL,
      showDeleteConfirmModal,
      isSectionEditable,
      selectedHints,
      isDataLoading,
      suggestions,
    } = this.state;
    const { jobProfileId } = params;
    return (
      <div>
        <JobProfileDetail
          {...this.props}
          hintsCS={hintsCS}
          hintsJPS={hintsJPS}
          hintsSJL={hintsSJL}
          dataLoading={isDataLoading}
          suggestions={suggestions}
          onEditDimensions={this.handleDimensionsEdit}
          onAddCSRespondent={this.handleAddCSRespondent}
          onToggleCS={this.handleToggleCS}
          onHintsCS={this.handleHintsCS}
          onToggleJPS={this.handleToggleJPS}
          onHintsJPS={this.handleHintsJPS}
          onHintsSJL={this.handleHintsSJL}
          sectionEditable={isSectionEditable}
          toggleSectionEditable={this.toggleSectionEditable}
          onSuggestJobProfile={this.handleSuggestJobProfile}
          onDeleteJobProfile={this.deleteJobProfile}
          jobId={jobProfileId}
          currentAccount={currentAccount}
          selectedHints={selectedHints}
        />
        <ConfirmModal
          modalOpen={showDeleteConfirmModal}
          reject={this.cancelDeletingJobProfile}
          confirm={this.processDeletingJobProfile}
          typeButton="btn-danger"
          match={match}
        />
      </div>
    );
  }
}

const JobProfileDetailGraphql = injectIntl(jobProfileCoreQuery(JobProfileDetailContainer));

JobProfileDetailContainer.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  jobProfileCore: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: intlShape.isRequired,
  growl: growlShape.isRequired,
  match: matchShape.isRequired,
  history: historyShape.isRequired,
};

export default withGrowl(
  withAccount(
    connect(
      mapStateToProps,
    )(JobProfileDetailGraphql),
  ),
);
